import React, { createContext, useEffect, useState } from "react"

import { BenchmarkTemplateManager } from "./components"
import MetricSelect from "./components/MetricsSelect/MetricSelect"
import { CODE_OPTIONS } from "./components/MetricsSelect/utils"
import CustomerDescription from "components/Shared/CustomerDescription"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { canShowCustomerInfo } from "./utils/utils"
import { LEVEL_NAME_DEFAULT, METRIC_SELECT_FEATURE_FLAG_ID } from "./constants"
import { LevelName, FilterLevelSelected } from "./interfaces"
import { Interval } from "components/Shared/PhronesisCalendar/PhronesisCalendar.domain"
import { isFeatureEnabled } from "config/feature-flags"
import { useIntl } from "react-intl"
import { Alert } from "@walmart-web/livingdesign-components"
import { disabledMetric } from "components/Shared/Filters/modules/Segmentation/constants"
import { MetricName } from "./components/MetricsSelect/interfaces"
import { CUSTOMER_TYPE } from "components/Shared/GlobalFilter/constants"

type TransactionalPerformanceProps = {
	period: string
	dateInterval: Interval
	levelName: LevelName
	labelFatherLevelName: string
	onChangeLevelName?: (levelName: LevelName) => void
	filterLevelSelected?: FilterLevelSelected
}

const EMPTY_FILTER_SELECTED = {
	filterLevelSelected: "",
	selectedFilters: [
		{
			code: "",
			name: "",
		},
	],
}

export const TransactionalPerformance: React.FC<TransactionalPerformanceProps> = (
	props
) => {
	const {
		period,
		dateInterval,
		levelName,
		labelFatherLevelName = LEVEL_NAME_DEFAULT,
		onChangeLevelName = () => undefined,
		filterLevelSelected = EMPTY_FILTER_SELECTED,
	} = props

	const intl = useIntl()

	const {
		filters,
		setFilters,
		moduleState,
		setModuleState,
	} = useFiltersContext()
	const {
		transactional: {
			benchmark: { metric },
		},
	} = moduleState

	const [showAllCustomerAlert, setShowAllCustomerAlert] = useState<boolean>(
		false
	)

	useEffect(() => {
		if (disabledMetric[metric as MetricName]) {
			setFilters({ ...filters, [CUSTOMER_TYPE]: [] })
		}
	}, [metric])

	const clientAndPenetration = () => {
		if (showAllCustomerAlert) {
			return (
				<div className="customer-description">
					<Alert className="customer-description-alert" variant="info">
						{intl.formatMessage(
							{
								id:
									"phronesis.transactional.application.growth-profiling.chart.description.allCustomer",
							},
							{ br: <br />, b: (str: any) => <b>{str}</b> }
						)}
						,
					</Alert>
				</div>
			)
		}
		if (
			canShowCustomerInfo([
				metric === CODE_OPTIONS.CLIENTS,
				metric === CODE_OPTIONS.PENETRATION,
				metric === CODE_OPTIONS.PURCHASE_FRECUENCY,
				metric === CODE_OPTIONS.AVG_SPEND,
				filters.age.length > 0,
				filters.gender.length > 0,
				filters.psychographics.length > 0,
			])
		) {
			return <CustomerDescription dateInterval={dateInterval} />
		}
	}

	return (
		<>
			{clientAndPenetration()}
			<MetricSelect
				isVisible={isFeatureEnabled(METRIC_SELECT_FEATURE_FLAG_ID)}
				onMetricChange={(option) => {
					setModuleState({
						...moduleState,
						transactional: {
							...moduleState.transactional,
							benchmark: {
								...moduleState.transactional.benchmark,
								metric: option.code,
							},
						},
					})
				}}
			/>
			<ShowAllCustomerAlertContext.Provider value={setShowAllCustomerAlert}>
				<BenchmarkTemplateManager
					metricName={metric}
					period={period}
					dateInterval={dateInterval}
					levelName={levelName}
					labelFatherLevelName={labelFatherLevelName}
					onChangeLevelName={onChangeLevelName}
					filterLevelSelected={filterLevelSelected}
				/>
			</ShowAllCustomerAlertContext.Provider>
		</>
	)
}

export const ShowAllCustomerAlertContext = createContext<
	(value: boolean) => void
>(() => {})

export default TransactionalPerformance
